import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, GraduationCap, Images, Newspaper, Users } from "lucide-react";
import { api, fmtDate, imgUrl } from "@/api";
import { useLang } from "@/i18n";

const BannerLink = ({ banner, className, imgClass, testId }) => {
  const img = <img src={imgUrl(banner.image)} alt={banner.title || "Banner"} className={imgClass} data-testid={testId} />;
  return banner.url ? (
    <a href={banner.url} target="_blank" rel="noopener noreferrer" className={className} data-testid={testId ? `${testId}-link` : undefined}>{img}</a>
  ) : (
    <div className={className}>{img}</div>
  );
};

const cardAnim = (i) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay: i * 0.08 },
});

export default function HomePage() {
  const { t, lang } = useLang();
  const [slides, setSlides] = useState([]);
  const [banners, setBanners] = useState([]);
  const [idx, setIdx] = useState(0);
  const [profile, setProfile] = useState(null);
  const [articles, setArticles] = useState([]);
  const [summary, setSummary] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    api.get("/slideshow").then((r) => setSlides(r.data)).catch(() => {});
    api.get("/banners").then((r) => setBanners(r.data)).catch(() => {});
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
    api.get("/articles").then((r) => setArticles(r.data.slice(0, 3))).catch(() => {});
    api.get("/alumni/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/galleries").then((r) => setGalleries(r.data.slice(0, 1))).catch(() => {});
    api.get("/programs").then((r) => setPrograms(r.data.slice(0, 4))).catch(() => {});
  }, []);

  useEffect(() => {
    if (slides.length < 2) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % slides.length), 5000);
    return () => clearInterval(id);
  }, [slides.length]);

  const excerpt = (profile?.[lang === "en" && profile?.content_en ? "content_en" : "content_id"] || "").split("\n")[0];

  const topBanner = banners.find((b) => b.position === "top");
  const leftBanner = banners.find((b) => b.position === "left");
  const rightBanner = banners.find((b) => b.position === "right");

  return (
    <div data-testid="home-page">
      {topBanner && <BannerLink banner={topBanner} className="block w-full" imgClass="w-full max-h-32 object-cover" testId="banner-top" />}
      {/* Hero slideshow 21:9 + banner samping */}
      <section className="relative bg-emerald-950">
        <div className="max-w-[1600px] mx-auto flex items-stretch xl:gap-4 xl:px-4">
          {leftBanner && (
            <aside className="hidden xl:block w-40 shrink-0 self-stretch" data-testid="banner-left">
              <BannerLink banner={leftBanner} className="block h-full" imgClass="h-full w-full object-cover rounded-xl" />
            </aside>
          )}
          <div className="relative flex-1 min-w-0 aspect-[21/9] max-h-[78vh] overflow-hidden">
          {slides.map((s, i) => (
            <div key={s.id} className="hero-slide absolute inset-0" style={{ opacity: i === idx ? 1 : 0 }} data-testid={`hero-slide-${i}`}>
              <img src={imgUrl(s.image)} alt={s.caption} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10 lg:p-14">
                <p className="text-[#D4AF37] text-xs sm:text-sm font-semibold tracking-widest uppercase mb-2">{t("home.tagline")}</p>
                <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight">
                  {lang === "en" && s.caption_en ? s.caption_en : s.caption}
                </h1>
              </div>
            </div>
          ))}
          {slides.length > 1 && (
            <>
              <button onClick={() => setIdx((idx - 1 + slides.length) % slides.length)} data-testid="hero-prev" aria-label="Previous" className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/40 hover:bg-[#D4AF37] hover:text-emerald-950 text-white flex items-center justify-center transition-colors">
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button onClick={() => setIdx((idx + 1) % slides.length)} data-testid="hero-next" aria-label="Next" className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-black/40 hover:bg-[#D4AF37] hover:text-emerald-950 text-white flex items-center justify-center transition-colors">
                <ChevronRight className="h-5 w-5" />
              </button>
              <div className="absolute bottom-4 right-6 flex gap-1.5">
                {slides.map((_, i) => (
                  <button key={i} onClick={() => setIdx(i)} data-testid={`hero-dot-${i}`} className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-6 bg-[#D4AF37]" : "w-2 bg-white/50"}`} />
                ))}
              </div>
            </>
          )}
          </div>
          {rightBanner && (
            <aside className="hidden xl:block w-40 shrink-0 self-stretch" data-testid="banner-right">
              <BannerLink banner={rightBanner} className="block h-full" imgClass="h-full w-full object-cover rounded-xl" />
            </aside>
          )}
        </div>
      </section>

      {/* Ikhtisar bento */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <motion.div {...cardAnim(0)} className="mb-12">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-emerald-950 tracking-tight">{t("home.summary_title")}</h2>
          <p className="text-slate-600 mt-2 text-sm sm:text-base">{t("home.summary_sub")}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {/* Profil */}
          <motion.div {...cardAnim(1)} className="md:col-span-2 bg-white rounded-xl border border-emerald-900/10 shadow-sm p-8 flex flex-col justify-between" data-testid="summary-profil">
            <div>
              <Users className="h-8 w-8 text-[#D4AF37] mb-4" />
              <h3 className="font-display text-2xl font-bold text-emerald-900 mb-3">{t("nav.profil")}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{excerpt || t("home.profil_excerpt")}</p>
            </div>
            <Link to="/profil" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 hover:text-[#D4AF37] transition-colors" data-testid="summary-profil-link">
              {t("common.read_more")} <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>

          {/* Alumni */}
          <motion.div {...cardAnim(2)} className="bg-emerald-900 rounded-xl shadow-sm p-8 text-white flex flex-col justify-between" data-testid="summary-alumni">
            <div>
              <GraduationCap className="h-8 w-8 text-[#D4AF37] mb-4" />
              <h3 className="font-display text-2xl font-bold mb-2">{t("nav.alumni")}</h3>
              <p className="text-5xl font-bold text-[#D4AF37]">{summary?.total ?? "—"}</p>
              <p className="text-sm text-emerald-100/80 mt-1">{t("alumni.total")}</p>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {["RA", "MI", "MTs", "MA"].map((tk) => (
                  <span key={tk} className="text-xs bg-white/10 rounded-full px-2.5 py-1">
                    {tk}: {summary?.by_tingkatan?.[tk] ?? 0}
                  </span>
                ))}
              </div>
            </div>
            <Link to="/alumni" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#D4AF37] hover:text-white transition-colors" data-testid="summary-alumni-link">
              {t("home.alumni_cta")} <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>

          {/* Galeri */}
          <motion.div {...cardAnim(3)} className="bg-white rounded-xl border border-emerald-900/10 shadow-sm p-6 flex flex-col" data-testid="summary-galeri">
            <Images className="h-8 w-8 text-[#D4AF37] mb-4" />
            <h3 className="font-display text-2xl font-bold text-emerald-900 mb-3">{t("nav.galeri")}</h3>
            {galleries[0] ? (
              <div className="grid grid-cols-3 gap-1.5 mb-3">
                {galleries[0].photos.slice(0, 3).map((p, i) => (
                  <img key={i} src={imgUrl(p)} alt="" className="aspect-square w-full object-cover rounded-md" />
                ))}
              </div>
            ) : (
              <div className="flex-1 rounded-md bg-emerald-50 mb-3" />
            )}
            <Link to="/galeri" className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 hover:text-[#D4AF37] transition-colors" data-testid="summary-galeri-link">
              {t("common.view_all")} <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>

          {/* Artikel */}
          <motion.div {...cardAnim(4)} className="md:col-span-2 bg-white rounded-xl border border-emerald-900/10 shadow-sm p-8" data-testid="summary-artikel">
            <Newspaper className="h-8 w-8 text-[#D4AF37] mb-4" />
            <h3 className="font-display text-2xl font-bold text-emerald-900 mb-4">{t("nav.artikel")}</h3>
            <ul className="space-y-3">
              {articles.map((a) => (
                <li key={a.id}>
                  <Link to={`/artikel/${a.id}`} className="group flex items-start justify-between gap-4" data-testid={`summary-article-${a.id}`}>
                    <div>
                      <p className="text-sm font-semibold text-slate-800 group-hover:text-emerald-800 transition-colors">{a.title}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{fmtDate(a.created_at, lang)} · {a.author_name}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 text-slate-300 group-hover:text-[#D4AF37] transition-colors mt-1" />
                  </Link>
                </li>
              ))}
            </ul>
            <Link to="/artikel" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 hover:text-[#D4AF37] transition-colors" data-testid="summary-artikel-link">
              {t("common.view_all")} <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>

          {/* Program */}
          <motion.div {...cardAnim(5)} className="bg-white rounded-xl border border-emerald-900/10 shadow-sm p-6" data-testid="summary-program">
            <h3 className="font-display text-2xl font-bold text-emerald-900 mb-4">{t("nav.program")}</h3>
            <ul className="space-y-3">
              {programs.slice(0, 3).map((p) => (
                <li key={p.id} className="border-l-2 border-[#D4AF37] pl-3">
                  <p className="text-sm font-semibold text-slate-800">{p.nama}</p>
                  <p className="text-xs text-slate-500">{p.pelaksanaan}</p>
                </li>
              ))}
            </ul>
            <Link to="/program" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-emerald-800 hover:text-[#D4AF37] transition-colors" data-testid="summary-program-link">
              {t("common.view_all")} <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>

          {/* PPDB */}
          <motion.div {...cardAnim(6)} className="bg-gradient-to-br from-emerald-800 to-emerald-950 rounded-xl shadow-sm p-6 text-white flex flex-col justify-between" data-testid="summary-ppdb">
            <div>
              <h3 className="font-display text-2xl font-bold mb-2">{t("nav.ppdb")}</h3>
              <p className="text-sm text-emerald-100/80">{t("home.ppdb_cta")}</p>
            </div>
            <Link to="/ppdb" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#D4AF37] hover:text-white transition-colors" data-testid="summary-ppdb-link">
              {t("common.read_more")} <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
